import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckIcon } from "@phosphor-icons/react";
import { Cpu, Sparkles } from "lucide-react";
import { useAction } from "convex/react";
import { api } from "convex/_generated/api";
import {
  ByomConfigForm,
  type ByomConfigFormState,
} from "@/components/settings/ai-provider";
import { useTranslation } from "@/packages/utils";

type ProviderType = "builtin_openai" | "byom_openai_compatible";

export interface AIProviderChoice {
  provider: ProviderType;
  byomConfig?: ByomConfigFormState;
}

interface AIProviderStepProps {
  onChoiceChange: (choice: AIProviderChoice | null, verified: boolean) => void;
}

const ease = [0.25, 0.46, 0.45, 0.94] as const;

const stagger = {
  hidden: { opacity: 0, y: 10 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.4,
      ease,
    },
  }),
};

interface TestResult {
  success: boolean;
  error?: string;
  latencyMs: number;
}

export function AIProviderStep({ onChoiceChange }: AIProviderStepProps) {
  const { t } = useTranslation("common");
  const testConnectionDirect = useAction(
    api.workspace_ai_provider_actions.testConnectionDirect,
  );

  const [selectedProvider, setSelectedProvider] =
    useState<ProviderType>("builtin_openai");
  const [byomConfig, setByomConfig] = useState<ByomConfigFormState | null>(
    null,
  );
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional mount-only effect
  useEffect(() => {
    onChoiceChange({ provider: "builtin_openai" }, true);
  }, []);

  const handleBuiltinClick = () => {
    setSelectedProvider("builtin_openai");
    onChoiceChange({ provider: "builtin_openai" }, true);
  };

  const handleByomClick = () => {
    setSelectedProvider("byom_openai_compatible");
    onChoiceChange(
      byomConfig ? { provider: "byom_openai_compatible", byomConfig } : null,
      false,
    );
  };

  const handleByomChange = (config: ByomConfigFormState) => {
    setByomConfig(config);
    onChoiceChange(
      { provider: "byom_openai_compatible", byomConfig: config },
      false,
    );
  };

  const handleTestResult = (result: TestResult | null) => {
    setTestResult(result);
    if (result?.success && byomConfig) {
      onChoiceChange({ provider: "byom_openai_compatible", byomConfig }, true);
    }
  };

  const handleTestConnection = async (config: ByomConfigFormState) => {
    setIsTesting(true);
    try {
      const result = await testConnectionDirect({
        endpointUrl: config.endpointUrl,
        modelName: config.modelName,
        apiKey: config.apiKey || undefined,
      });
      setTestResult(result);
      if (result.success) {
        onChoiceChange(
          { provider: "byom_openai_compatible", byomConfig: config },
          true,
        );
      }
    } finally {
      setIsTesting(false);
    }
  };

  const builtinSelected = selectedProvider === "builtin_openai";
  const byomSelected = selectedProvider === "byom_openai_compatible";

  return (
    <div className="flex flex-col">
      <motion.div custom={0} variants={stagger} initial="hidden" animate="show">
        <h1 className="text-[26px] font-bold tracking-[-0.02em] text-primary">
          {t("onboarding.aiProvider.title")}
        </h1>
        <p className="mt-2 text-[14px] text-secondary leading-relaxed">
          {t("onboarding.aiProvider.description")}
        </p>
      </motion.div>

      <div className="mt-8 flex flex-col gap-3">
        {/* Card 1: Built-in AI */}
        <motion.button
          type="button"
          custom={1}
          variants={stagger}
          initial="hidden"
          animate="show"
          onClick={handleBuiltinClick}
          className={`w-full text-left rounded-xl border p-4 transition-all duration-200 ${
            builtinSelected
              ? "border-accent bg-accent/8 shadow-sm"
              : "border-border/50 bg-white hover:bg-card-hover hover:border-border cursor-pointer"
          }`}
        >
          <div className="flex items-start gap-3">
            <div
              className={`mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg ${
                builtinSelected
                  ? "bg-accent/15 text-accent"
                  : "bg-muted text-secondary"
              }`}
            >
              <Sparkles className="size-4" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p
                  className={`text-[14px] font-semibold ${builtinSelected ? "text-accent" : "text-primary"}`}
                >
                  {t("onboarding.aiProvider.builtinLabel")}
                </p>
                {builtinSelected && (
                  <CheckIcon className="size-4 text-accent" />
                )}
              </div>
              <p className="mt-0.5 text-[13px] text-secondary leading-relaxed">
                {t("onboarding.aiProvider.builtinDescription")}
              </p>
            </div>
          </div>
        </motion.button>

        {/* Card 2: Connect your own model */}
        <motion.button
          type="button"
          custom={2}
          variants={stagger}
          initial="hidden"
          animate="show"
          onClick={handleByomClick}
          className={`w-full text-left rounded-xl border p-4 transition-all duration-200 ${
            byomSelected
              ? "border-accent bg-accent/8 shadow-sm"
              : "border-border/50 bg-white hover:bg-card-hover hover:border-border cursor-pointer"
          }`}
        >
          <div className="flex items-start gap-3">
            <div
              className={`mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg ${
                byomSelected
                  ? "bg-accent/15 text-accent"
                  : "bg-muted text-secondary"
              }`}
            >
              <Cpu className="size-4" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p
                  className={`text-[14px] font-semibold ${byomSelected ? "text-accent" : "text-primary"}`}
                >
                  {t("onboarding.aiProvider.byomLabel")}
                </p>
                {byomSelected && <CheckIcon className="size-4 text-accent" />}
              </div>
              <p className="mt-0.5 text-[13px] text-secondary leading-relaxed">
                {t("onboarding.aiProvider.byomDescription")}
              </p>
            </div>
          </div>
        </motion.button>
      </div>

      {/* BYOM Config Form */}
      {byomSelected && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="mt-6"
        >
          <ByomConfigForm
            onChange={handleByomChange}
            onTestResult={handleTestResult}
            onTestConnection={handleTestConnection}
            testResult={testResult}
            isTesting={isTesting}
          />
        </motion.div>
      )}
    </div>
  );
}
